// /home/cockskiz/new.greatlysocial.com/frontend/src/pages/dashboard/accounts/modals/PinterestModals.js

import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * Props from Accounts.js typically include:
 * 
 * showPinterestModal, setShowPinterestModal,
 * pinterestAccounts, selectedPinterestIds,
 * setSelectedPinterestIds,
 * 
 * showPinterestDeleteModal, setShowPinterestDeleteModal,
 * showPinterestDeleteConfirmModal, setShowPinterestDeleteConfirmModal,
 * 
 * plus possibly "accounts" so we can reference the connected Pinterest pages for delete
 */
export default function PinterestModals({
  showPinterestModal,
  setShowPinterestModal,
  pinterestAccounts,
  selectedPinterestIds,
  setSelectedPinterestIds,

  showPinterestDeleteModal,
  setShowPinterestDeleteModal,
  showPinterestDeleteConfirmModal,
  setShowPinterestDeleteConfirmModal,

  // The parent’s main "accounts" array, to list connected accounts for delete
  accounts,
}) {
  // 1) For the "Connect" flow, we show the pending Pinterest data
  function handleCheckboxChange(pid) {
    setSelectedPinterestIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]
    );
  }

  function handleConnectSelectedPinterest() {
    if (selectedPinterestIds.length === 0) {
      alert('Please select at least one Pinterest account to connect.');
      return;
    }

    // Build payload with everything your route expects
    const selectedPinterestPayload = pinterestAccounts
      .filter((acc) => selectedPinterestIds.includes(acc.pinterestId))
      .map((acc) => ({
        pinterestId: acc.pinterestId,
        username: acc.username,
        profileImage: acc.profileImage,
        accessToken: acc.accessToken,
        refreshToken: acc.refreshToken,
        expiresInSeconds: acc.expiresInSeconds,
        refreshExpiresInSeconds: acc.refreshExpiresInSeconds,
      }));

    fetch('/api/auth/pinterest/accounts/connect', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedPinterestAccounts: selectedPinterestPayload }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('Connect Pinterest response:', data);
        setShowPinterestModal(false);
        setSelectedPinterestIds([]);
        window.location.href = 'https://greatlysocial.com/dashboard/accounts';
      })
      .catch((err) => console.error('Error connecting Pinterest accounts:', err));
  }

  // 2) For the "Delete" flow, we display the connected Pinterest pages from the main "accounts" array
  function handleDeleteCheck(pid) {
    setSelectedPinterestIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]
    );
  }

  function handleDeletePinterestAccounts() {
    if (selectedPinterestIds.length === 0) {
      alert('Please select at least one Pinterest account to delete.');
      return;
    }
    // close the first "delete" modal, show the confirm
    setShowPinterestDeleteModal(false);
    setShowPinterestDeleteConfirmModal(true);
  }

  function handleDeletePinterestConfirm() {
    const selectedPayload = selectedPinterestIds.map((id) => ({ pinterestId: id }));

    fetch('/api/auth/pinterest/accounts/delete', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedPinterestAccounts: selectedPayload }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('Delete Pinterest response:', data);
        setShowPinterestDeleteConfirmModal(false);
        setSelectedPinterestIds([]);
        window.location.href = 'https://greatlysocial.com/dashboard/accounts';
      })
      .catch((err) => console.error('Error deleting Pinterest accounts:', err));
  }

  // We'll get the connected Pinterest accounts from your main "accounts" array:
  const pinterestAcc = accounts?.find((a) => a.platform.toLowerCase() === 'pinterest');
  const connectedPinterestPages = pinterestAcc?.connectedPages || [];

  return (
    <>
      {/* ********** CONNECT PINTEREST MODAL ********** */}
      <Modal show={showPinterestModal} onHide={() => setShowPinterestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Pinterest Account(s)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!pinterestAccounts || pinterestAccounts.length === 0 ? (
            <p>No Pinterest accounts found in session.</p>
          ) : (
            <ul className="list-group">
              {pinterestAccounts.map((acc) => {
                const checked = selectedPinterestIds.includes(acc.pinterestId);
                return (
                  <li key={acc.pinterestId} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleCheckboxChange(acc.pinterestId)}
                    />
                    {acc.profileImage && (
                      <img
                        src={acc.profileImage}
                        alt={acc.username || acc.pinterestId}
                        style={{ width: 30, height: 30, borderRadius: '50%', marginRight: 8 }}
                      />
                    )}
                    <span>{acc.username || acc.pinterestId}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPinterestModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnectSelectedPinterest}>
            Connect
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ********** DELETE PINTEREST MODAL ********** */}
      <Modal show={showPinterestDeleteModal} onHide={() => setShowPinterestDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Pinterest Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select which Pinterest accounts to remove.</p>
          {connectedPinterestPages.length === 0 ? (
            <p>No connected Pinterest accounts to delete.</p>
          ) : (
            <ul className="list-group">
              {connectedPinterestPages.map((item) => {
                const pid = item.pinterestId; // or item.pinterestId
                const checked = selectedPinterestIds.includes(pid);
                return (
                  <li key={pid} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleDeleteCheck(pid)}
                    />
                    {item.profileImage && (
                      <img
                        src={item.profileImage}
                        alt={item.username || pid}
                        style={{ width: 30, height: 30, borderRadius: '50%', marginRight: 8 }}
                      />
                    )}
                    <span>{item.username || pid}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPinterestDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeletePinterestAccounts}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ********** CONFIRM DELETE PINTEREST MODAL ********** */}
      <Modal
        show={showPinterestDeleteConfirmModal}
        onHide={() => setShowPinterestDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these Pinterest accounts?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPinterestDeleteConfirmModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleDeletePinterestConfirm}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
